import type { EntityPropertySelectionEntry } from "~/models/polyschema/EntityPropertySelectionEntry"

import { metapropertyEntityId, PropertyGroup, PropertyId } from "."
import { EntityPropertyGroupIdBuf } from "../generated/entity"

function T(groupId: number | EntityPropertyGroupIdBuf | string, propertyId: number | string) {
  if (typeof groupId !== "string")
    groupId = groupId.toString(16).padStart(8, "0")
  else if (groupId.length !== 8)
    throw new Error("Invalid group id string provided")

  if (typeof propertyId === "number") {
    const uuid = numberToUuid(propertyId)
    return `${groupId}-${uuid}`
  }
  else if (propertyId.length !== 36) // uuid v7
    throw new Error("Invalid uuid string provided")
  else
    return `${groupId}-${propertyId}`
}

const UUID_MIN = "00000000-0000-0000-0000-000000000000"
const UUID_MAX = "ffffffff-ffff-ffff-ffff-ffffffffffff"
const GROUP_MIN = "00000000"
const GROUP_MAX = "ffffffff"

/**
 * TODO: This implementation does not currently have the boundary awareness like in the
 * rust implementation. So we can't actually correctly merge ranges, yet.
 */
export class InterfaceSelection {
  _selection: EntityPropertySelection

  constructor(selection: EntityPropertySelection) {
    this._selection = selection
  }

  inner(): Readonly<EntityPropertySelection> {
    return this._selection
  }

  static nothing() {
    return new InterfaceSelection({ ranges: {} })
  }

  static everything() {
    const section: EntityPropertySelectionEntry = { type: "section" }
    return new InterfaceSelection({
      ranges: { [T(GROUP_MIN, UUID_MIN)]: [T(GROUP_MAX, UUID_MAX), section] },
    })
  }

  addPropertyRange(start: string, end: string, entry?: EntityPropertySelectionEntry) {
    entry = entry ?? { type: "section" }
    if (start.length !== 45 || end.length !== 45) // entity id extended uuid
      throw new Error(`Invalid range provided: ${start}:${end}`)

    const trueStart = start
    const trueEnd = end
    for (const [rangeStart, [rangeEnd, _]] of Object.entries(this._selection.ranges)) {
      if (rangeStart <= start && rangeEnd >= end)
        return
      else if (rangeStart <= start && rangeEnd >= start) {
        start = rangeStart
        delete this._selection.ranges[rangeStart]
      }
      else if (rangeStart <= end && rangeEnd >= end) {
        end = rangeEnd
        delete this._selection.ranges[rangeStart]
      }
      else if (rangeStart >= start && rangeEnd <= end) {
        delete this._selection.ranges[rangeStart]
      }
    }
    this._selection.ranges[trueStart] = [trueEnd, entry]
  }

  addTimelineRange(from: string, to: string, limit: number, descending: boolean) {
    if (from.length !== 45 || to.length !== 45) // entity id extended uuid
      throw new Error(`Invalid range provided: ${from}:${to}`)
    const entry: EntityPropertySelectionEntry = { type: "timeline", limit, descending }
    this.addPropertyRange(from, to, entry)
  }

  /**
   * Currently does not do merging of selections or other fancy stuff.
   */
  addPropertyId(propertyId: PropertyId) {
    this.addPropertyRange(propertyId, propertyId)
  }

  hasRange(start: string, end: string) {
    if (start.length !== 45 || end.length !== 45) // entity id extended uuid
      throw new Error(`Invalid range provided: ${start}:${end}`)
    for (const [rangeStart, [rangeEnd, _]] of Object.entries(this._selection.ranges)) {
      if (rangeStart <= start && rangeEnd >= end)
        return true
    }
    return false
  }

  hasEntireGroup(groupId: PropertyGroup) {
    return this.hasRange(T(groupId, UUID_MIN), T(groupId, UUID_MAX))
  }

  hasPropertyId(propertyId: PropertyId) {
    return this.hasRange(propertyId, propertyId)
  }

  withSelfRole() {
    const { state: myself } = useSelf()
    if (myself.value.user) {
      // NOTE: We can't import BaseFile due to import annoyances.
      const propertyId = metapropertyEntityId(
        EntityPropertyGroupIdBuf.SharedDriveUsersMetaProperty,
        myself.value.user.id,
      )
      this.addPropertyId(propertyId)
    }
    return this
  }

  withGroupTimeline(groupId: PropertyGroup, limit: number, descending = true) {
    this.addTimelineRange(
      T(groupId, UUID_MIN),
      T(groupId, UUID_MAX),
      limit,
      descending,
    )
    return this
  }

  withFileComments(limit: number, descending = true) {
    this.addTimelineRange(
      T(PropertyGroup.FileCommentsMetaProperty, UUID_MIN),
      T(PropertyGroup.FileCommentsMetaProperty, UUID_MAX),
      limit,
      descending,
    )
    return this
  }

  static selectedProperties = (
    properties: Array<PropertyId>,
    groups: Array<PropertyGroup> = [],
  ) => {
    const selection = InterfaceSelection.nothing()
    for (const property of properties) {
      selection.addPropertyId(property)
    }
    for (const groupId of groups)
      selection.addEntireGroup(groupId)
    return selection
  }

  static selectedGroups = (groups: Array<PropertyGroup>) => {
    const selection = InterfaceSelection.nothing()
    for (const groupId of groups)
      selection.addEntireGroup(groupId)
    return selection
  }

  addEntireGroup(groupId: PropertyGroup) {
    this.addPropertyRange(T(groupId, UUID_MIN), T(groupId, UUID_MAX))
  }

  mergedWith(other: InterfaceSelection): InterfaceSelection {
    for (const [start, [end, entry]] of Object.entries(other._selection.ranges))
      this.addPropertyRange(start, end, entry)
    return this
  }

  static minimumPathSelection() {
    return InterfaceSelection.selectedGroups(
      [
        PropertyGroup.FileClientOnlyProperties,
        PropertyGroup.FolderDerivedSizingProperties,
        PropertyGroup.FileSystemPathProperties,
        PropertyGroup.FileCoreProperties,
        PropertyGroup.FileDerivedProperties,
        PropertyGroup.FileFolderExtendedProperties,
        PropertyGroup.FileFolderExtendedDerivedProperties,
      ],
    ).withSelfRole()
  }

  /**
   * @returns The minimum set of properties needed to show a file in a file item element,
   * such as in a history view, in the agent chat, etc.
   */
  static minimumFileItemSelection() {
    return InterfaceSelection.selectedProperties(
      [PropertyId.FILE_MIME_TYPE, PropertyId.FILE_NAME, PropertyId.THUMBNAIL_SET],
      [PropertyGroup.FileSystemPathProperties],
    ).withSelfRole()
  }

  static minimumAutocompleteSelection() {
    return InterfaceSelection.selectedGroups(
      [PropertyGroup.FileSystemPathProperties, PropertyGroup.FileCoreProperties, PropertyGroup.FileDerivedProperties],
    ).withSelfRole()
  }

  /**
   * The minimum set of properties generally needed to show the file in the UI,
   * i.e. collection view.
   */
  static minimumInterfaceSelection() {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.FileClientOnlyProperties,
      PropertyGroup.FolderDerivedSizingProperties,
      PropertyGroup.FileSystemPathProperties,
      PropertyGroup.FileCoreProperties,
      PropertyGroup.FileDerivedProperties,
    ]).withSelfRole()
  }

  static minimumSharedDriveHomeSelection() {
    return InterfaceSelection.selectedProperties(
      [PropertyId.FILE_MIME_TYPE, PropertyId.FILE_NAME, PropertyId.THUMBNAIL_SET],
      [PropertyGroup.FileSystemPathProperties, PropertyGroup.SharedDriveUsersMetaProperty],
    ).withSelfRole()
  }

  /**
   * This seems intimidating but its actually a single db query since
   * these are almost all contiguous property groups.
   */
  static minimumPropertiesViewSelection(numComments = 3) {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.FolderDerivedSizingProperties,
      PropertyGroup.FileSystemPathProperties,
      PropertyGroup.FileCoreProperties,
      PropertyGroup.FileDerivedProperties,
      PropertyGroup.FileSecondaryDerivedProperties,
      PropertyGroup.FileExtendedProperties,
      PropertyGroup.FileTagMetaProperty,
      PropertyGroup.FileFolderExtendedProperties,
      PropertyGroup.FileFolderExtendedDerivedProperties,
      PropertyGroup.FileImageExtendedProperties,
      PropertyGroup.FileImageExtendedDerivedProperties,
      PropertyGroup.FileAudioExtendedProperties,
      PropertyGroup.FileAudioExtendedDerivedProperties,
      PropertyGroup.FileVideoExtendedProperties,
      PropertyGroup.FileVideoExtendedDerivedProperties,
      PropertyGroup.FileThreeDimensionalExtendedProperties,
      PropertyGroup.FileThreeDimensionalExtendedDerivedProperties,
      PropertyGroup.FileLinkExtendedProperties,
      PropertyGroup.FileLinkExtendedDerivedProperties,
      PropertyGroup.FilePdfExtendedProperties,
      PropertyGroup.FilePdfExtendedDerivedProperties,
      PropertyGroup.FileTextExtendedProperties,
      PropertyGroup.FileTextExtendedDerivedProperties,
      PropertyGroup.FilePresentationExtendedProperties,
      PropertyGroup.FilePresentationExtendedDerivedProperties,
      PropertyGroup.FileDocumentExtendedProperties,
      PropertyGroup.FileDocumentExtendedDerivedProperties,
      PropertyGroup.FileSpreadsheetExtendedProperties,
      PropertyGroup.FileSpreadsheetExtendedDerivedProperties,
    ])
      .withFileComments(numComments)
      .withSelfRole()
  }

  /**
   * Minimum set of property groups needed to show a file in the transcript view in
   * the agent:
   *
   * mime type, file name, thumbnails, width / height.
   *
   * TODO: for shared drives this may include more things.
   */
  static minimumTranscriptSelection() {
    return InterfaceSelection.selectedGroups(
      [PropertyGroup.FileSystemPathProperties, PropertyGroup.FileCoreProperties, PropertyGroup.FileDerivedProperties],
    ).withSelfRole()
  }

  /**
   * The minimum set of properties that are required to get
   * information about a folder in order to do things like
   * navigation, etc.
   *
   * TODO: only retrieve notes if we have the notes view.
   * TODO: folder icons?
   */
  static minimumClientFolderProperties() {
    return InterfaceSelection.selectedProperties(
      [PropertyId.FILE_NOTES],
      [PropertyGroup.FileFolderExtendedProperties],
    ).withSelfRole()
  }

  /**
   * The minimum selection for the user's SelfConversations
   * endpoint. Notably, we don't need the entire transcript.
   */
  static minimumSelfConversationSelection() {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.ConversationCoreProperties,
      PropertyGroup.ConversationSharingUsersMetaProperty,
      PropertyGroup.ConversationClientOnlyProperties,
    ])
  }

  static minimumSelfSidebarSelection() {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.FileDerivedProperties,
      PropertyGroup.FileSystemPathProperties,
      PropertyGroup.FileCoreProperties,
      PropertyGroup.FileDerivedProperties,
    ]).withSelfRole()
  }

  /**
   * The minimum set of properties when doing a conversation
   * properties request, e.g. when we load the conversation
   * on the sidebar.
   */
  static minimumConversationViewSelection() {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.ConversationCoreProperties,
      PropertyGroup.ConversationTurnsMetaProperty,
      PropertyGroup.ConversationSharingUsersMetaProperty,
    ])
  }

  /**
   * The minimum set of properties for viewing a conversation in the
   * main nav view
   */
  static minimumConversationNavViewSelection() {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.ConversationCoreProperties,
      PropertyGroup.ConversationSharingUsersMetaProperty,
    ])
  }

  static minimumAgentFileRefSelection() {
    return InterfaceSelection.selectedProperties([
      PropertyId.FILE_MIME_TYPE,
      PropertyId.FILE_NAME,
      PropertyId.THUMBNAIL_SET,
    ], [PropertyGroup.FileSystemPathProperties])
  }

  static minimumSearchInterfaceViewSelection() {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.SearchCoreProperties,
      PropertyGroup.SearchDerivedProperties,
    ])
  }

  static minimumCachedSearchViewSelection() {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.SearchCoreProperties,
      PropertyGroup.SearchDerivedProperties,
      PropertyGroup.SearchClientResultsMetaProperty,
    ])
  }
}
